import type { HealthCardPhoto, HealthCardPhotoList } from "@emr/contracts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import {
  appointmentHealthCardPhotosKey,
  type HealthCardPhotosClient,
  isHealthCardPhotosHidden,
} from "../../api/healthCardPhotos";
import { HealthCardPhotoImage } from "../../components/HealthCardPhotoField";
import "../../components/health-card-photo.css";
import { AUDITED_READ } from "./intakeReviewQueries";
import { formatInClinic } from "./scheduleTime";

/** Who sent or took the photo, and when, on the clinic's clock. */
export function healthCardPhotoOrigin(photo: HealthCardPhoto, timeZone: string): string {
  const when = formatInClinic(photo.capturedAt, timeZone, {
    dateStyle: "medium",
    timeStyle: "short",
  });
  return photo.source === "staff"
    ? `Taken by ${photo.capturedByName ?? "a staff member"} · ${when}`
    : `Sent from the patient's questionnaire link · ${when}`;
}

export type IntakeHealthCardPhotoRoutes = Pick<
  HealthCardPhotosClient,
  "listAppointmentHealthCardPhotos" | "openHealthCardPhoto"
>;

export type IntakeHealthCardPhotosQuery = UseQueryResult<HealthCardPhotoList, Error>;

/**
 * The booking's or walk-in's health card photos (PA-13), read once a submitted questionnaire is on
 * screen (as the photo used to be read when its panel mounted), never on the schedule poll. The
 * dialog reads the list itself so it can number the Card photo step only when there is one.
 */
export function useIntakeHealthCardPhotos(
  appointmentId: string,
  siteId: string,
  routes: IntakeHealthCardPhotoRoutes | undefined,
  enabled: boolean,
): IntakeHealthCardPhotosQuery {
  return useQuery({
    queryKey: appointmentHealthCardPhotosKey(siteId, appointmentId),
    queryFn: ({ signal }) => {
      if (routes === undefined) throw new Error("Health card photos are unavailable.");
      return routes.listAppointmentHealthCardPhotos(appointmentId, signal);
    },
    enabled: enabled && routes !== undefined,
    ...AUDITED_READ,
  });
}

/**
 * Whether Review intake shows its Card photo step: the patient sent a photo, or the list failed
 * for a reason other than the role (a role that may not see photos sees no step at all).
 */
export function intakeHealthCardPhotoShown(photos: IntakeHealthCardPhotosQuery): boolean {
  if (photos.isError) return !isHealthCardPhotosHidden(photos.error);
  return photos.isSuccess && photos.data.photos[0] !== undefined;
}

/**
 * Review intake's Card photo step (PA-13): the newest photo the patient sent from this booking's
 * or walk-in's questionnaire link. A clean photo's bytes are one audited read, made when the photo
 * is put on screen (each time the review opens) and dropped when it closes; one still being
 * checked or blocked by the safety check says so instead. Capture only: nothing reads the card.
 */
export function IntakeHealthCardPhoto({
  photos,
  timeZone,
  client,
}: {
  readonly photos: IntakeHealthCardPhotosQuery;
  readonly timeZone: string;
  readonly client: Pick<HealthCardPhotosClient, "openHealthCardPhoto">;
}) {
  if (photos.isError) {
    if (isHealthCardPhotosHidden(photos.error)) return null;
    return (
      <div className="hcp">
        <p className="hcp-problem" role="alert">
          The card photo could not be loaded.{" "}
          <button type="button" className="hcp-btn" onClick={() => void photos.refetch()}>
            Try again
          </button>
        </p>
      </div>
    );
  }
  const newest = photos.data?.photos[0];
  if (newest === undefined) return null;
  return (
    <div className="hcp">
      {newest.status === "clean" ? (
        <HealthCardPhotoImage
          key={newest.id}
          client={client}
          photoId={newest.id}
          alt="Front of the patient's health card"
        />
      ) : null}
      <p className="hcp-meta">{healthCardPhotoOrigin(newest, timeZone)}</p>
      {newest.status === "pending_scan" ? (
        <p className="hcp-row">
          <span className="hcp-state" role="status">
            Safety check running. The photo shows once it passes.
          </span>
          <button type="button" className="hcp-btn" onClick={() => void photos.refetch()}>
            Check again
          </button>
        </p>
      ) : null}
      {newest.status === "blocked" ? (
        <p className="hcp-problem">
          Blocked by the safety check. Take a new photo with Add photo in the chart's Details.
        </p>
      ) : null}
    </div>
  );
}
